import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';

export const SPLITTER = '+';

// Chromium에서는 Meta로 통일이 되었으나 Firefox에서는 Meta대신 OS를 사용
// https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_code_values
// Voice toolkit의 경우 Chromium 기반으로 개발되어 있어 Meta로 통일
// TODO: IE에서도 metaKey로 동작하는지 확인 필요
const FUNCTIONAL_KEYS = ['meta', 'shift', 'ctrl', 'alt'];

const getFunctionalKeys = (event: KeyboardEvent) =>
  FUNCTIONAL_KEYS.filter(
    (key) => event[`${key}Key` as keyof typeof event]
  ).sort();

/**
 * The single spelling rule for a shortcut string: lowercase, and the modifiers
 * sorted so the caller's order is irrelevant. The non-modifier key is taken
 * off the END of the string and re-appended after the sort, so it is never
 * swept into it ('shift+ctrl+l' -> 'ctrl+shift+l', never 'ctrl+l+shift').
 *
 * ISSUE-041 defect (a): this used to be inlined in `register` only, while
 * `unRegister` merely lowercased. `unRegister('shift+alt+a')` therefore could
 * not remove a registration stored as 'alt+shift+a', and the entry leaked for
 * the provider's lifetime. Both sides now call this, which is what makes them
 * symmetric. Exported so the rule can be asserted directly rather than only
 * inferred from a dispatch.
 */
export const normalizeShortcut = (shortcut: string) => {
  const [eventKey, ...functionalKeys] = shortcut
    .toLocaleLowerCase()
    .split(SPLITTER)
    .reverse();
  return [...functionalKeys.sort(), eventKey].join(SPLITTER);
};

const isEditorFocused = (target: EventTarget | null) => {
  if (!target) return false;
  let element = target as HTMLElement;
  return (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element.isContentEditable
  );
};

// Controls that activate on Space/Enter — the user agent does it for `button`,
// `select` and `a[href]`, a component would have to do it for the ARIA roles.
// Every entry is pinned by a test in HotkeyManagerContextProvider.test.tsx
// against an element only that entry can match, which pins the SELECTOR; what
// it does NOT establish is that the app needs the entry, so here is the
// grounding as of ISSUE-044 (reviewed 2026-08-30). Reachable in `src/` today:
// `button`, and `[role="combobox"]` — `Select.tsx:162` is a
// `<button role="combobox">` and matches on either. Present but inert:
// `[role="listbox"]` (`Dropdown/DropdownList.tsx:10`) and `[role="option"]`
// (`Dropdown/DropdownItem.tsx:41`) are a non-focusable `<ul>`/`<li>` with no
// keydown handler. No instance at all: `select`, `a[href]`, `[role="menuitem"]`.
// ISSUE-044's Scope names `button`, `select`, `a[href]`, `[role="combobox"]`
// and `[role="option"]` for Space/Enter ("at minimum ..."), so those five stay
// as forward cover. `[role="listbox"]` is in that Scope's ARROW list only and
// `[role="menuitem"]` is in neither — those two are the entries carrying
// neither an instance nor a Scope line.
const ACTIVATION_CONTROLS =
  'button, select, a[href], [role="combobox"], [role="listbox"], [role="option"], [role="menuitem"]';
// Controls that navigate with the arrow keys. Deliberately a different, and
// smaller, set: a plain `button` does not move on an arrow, so FR-074's
// left/right panel switching must keep working while a button has focus.
// `[role="slider"]` is the audio timeline, which moves the playhead on
// ArrowLeft/ArrowRight — `src/components/AudioEditor/Editor.tsx:342-366`.
const ARROW_CONTROLS =
  'select, [role="combobox"], [role="listbox"], [role="slider"]';
const ACTIVATION_KEYS = ['Space', 'Enter', 'NumpadEnter'];

/**
 * True when the focused control owns this key natively, so a global hotkey
 * must not consume it (FR-072). Distinct from `isEditorFocused`, which is
 * "text entry: suppress everything": this suppresses one key at a time.
 *
 * `closest` rather than `instanceof`, because the provider listens on `window`
 * in the bubble phase and the target can be a child element (the `<span>`
 * inside a button, the `<svg>` icon inside a combobox).
 *
 * Takes the RAW `event.code` ('Space', 'ArrowRight', 'NumpadEnter'), never the
 * normalized shortcut key: the strip in the handler turns those into 'space',
 * 'right' and 'enter', which match neither `ACTIVATION_KEYS` nor the
 * `startsWith('Arrow')` test, so the whole guard would fail open.
 */
const nativeKeyConflict = (target: EventTarget | null, code: string) => {
  if (!(target instanceof Element)) return false;
  if (ACTIVATION_KEYS.includes(code)) {
    return !!target.closest(ACTIVATION_CONTROLS);
  }
  if (code.startsWith('Arrow')) return !!target.closest(ARROW_CONTROLS);
  return false;
};

type Registration = { owner: string; callback: () => void };

/**
 * One entry per normalized shortcut, holding EVERY owner that has registered
 * it, in registration order. Newest wins (ISSUE-001), so the live callback is
 * the last element — and because the earlier owners are still there, removing
 * the newest restores the previous one instead of killing the shortcut.
 */
type HotkeyRegistry = Map<string, Registration[]>;

/** The live owner of a shortcut is the newest one still registered. */
const liveCallback = (registry: HotkeyRegistry, shortcut: string) => {
  const owners = registry.get(shortcut);
  return owners && owners.length
    ? owners[owners.length - 1].callback
    : undefined;
};

/**
 * The context carries the OWNER-AWARE primitives. Consumers do not use them
 * directly — `useHotkeyManager` below binds the owner — because an owner
 * passed by hand is an owner a caller can get wrong, and defaulting it (to the
 * shortcut string, or to one shared constant) silently reinstates defect (b).
 */
interface HotkeyRegistryApi {
  register: (owner: string, shortcut: string, callback: () => void) => void;
  unRegister: (owner: string, shortcut: string) => void;
}

export interface HotKeyManager {
  register: (shortcut: string, callback: () => void) => void;
  unRegister: (shortcut: string) => void;
}

export const HotkeyManagerContext = createContext<HotkeyRegistryApi>(
  {} as HotkeyRegistryApi
);

/**
 * The consumer-facing API, and the only supported way to reach the registry.
 * `useId` mints a token per mounted component instance, so two mounted
 * components are two owners even when they bind the same shortcut — which is
 * the everyday case on /cvc, where `FilePlayer` and `AudioEditorPanel` both
 * hold `'space'`. `register`/`unRegister` keep the signatures they have always
 * had, so a consumer migrates by swapping how it obtains them and nothing
 * else.
 *
 * Both are BLOCK-bodied on purpose: they must return `undefined`, because six
 * consumers and two test harnesses write `useEffect(() => register(...), [])`
 * with an implicit return, and an effect callback that returns anything other
 * than a function throws. An expression body here would hand back whatever the
 * registry's `Map.set` returns (the Map).
 *
 * The returned object is memoized, so a consumer effect that lists `register`
 * or `unRegister` in its dependency array does not re-run when the provider
 * re-renders.
 */
export const useHotkeyManager = (): HotKeyManager => {
  const registry = useContext(HotkeyManagerContext);
  const owner = useId();

  return useMemo(
    () => ({
      register: (shortcut: string, callback: () => void) => {
        registry.register(owner, shortcut, callback);
      },
      unRegister: (shortcut: string) => {
        registry.unRegister(owner, shortcut);
      },
    }),
    [registry, owner]
  );
};

export const HotkeyManagerContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  // A ref, not state: a registration must not re-render the provider. That is
  // ISSUE-041 defect (c) — the listener effect used to depend on the
  // registration array, so `window`'s keydown listener was torn down and
  // re-attached on every register(), and a CVC mount registers 12+ shortcuts.
  // Nothing outside this file reads the registry, so nothing depended on the
  // re-render either.
  const registryRef = useRef<HotkeyRegistry>(new Map());

  // Empty deps: attached once per provider, removed once on unmount. The
  // handler reads the ref at DISPATCH time, so it always sees the current
  // registrations without the effect having to know about them.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const eventKey = event.code
        .trim()
        .toLocaleLowerCase()
        .replace(/key|digit|numpad|arrow/, '');
      const functionalKeys = getFunctionalKeys(event);
      if (functionalKeys.length > 0) {
        // Deliberately NOT behind the guards below (ISSUE-044, outcome (b)):
        // a modifier shortcut stays live inside a text field, which is what
        // lets Alt+1..Alt+4 / F1..F4 toggle panels and Ctrl+S save the audio
        // edit while the caret sits in a file-name input.
        const shortcut = [...functionalKeys, eventKey].join(SPLITTER);
        liveCallback(registryRef.current, shortcut)?.();
      } else {
        // Both guards run BEFORE the preventDefault below: skipping the
        // callback while still cancelling the default would leave the key
        // doing nothing at all, which is worse than no guard.
        if (
          isEditorFocused(event.target) ||
          nativeKeyConflict(event.target, event.code)
        ) {
          return;
        }
        const callback = liveCallback(registryRef.current, eventKey);
        if (callback) {
          // Space and the arrows scroll the page. No focused control owns them
          // here — `nativeKeyConflict` already returned above for the ones that
          // do — so the shortcut takes them and suppresses the scroll.
          if (event.code === 'Space' || event.code.includes('Arrow')) {
            event.preventDefault();
          }
          callback();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  const register = useCallback(
    (owner: string, shortcut: string, callback: () => void) => {
      const key = normalizeShortcut(shortcut);
      const owners = registryRef.current.get(key) || [];
      // Dropping this owner's previous entry before appending keeps it to one
      // entry per shortcut, and moves it to the end — so re-registering makes
      // an owner the newest again, the way re-registering has always won.
      registryRef.current.set(key, [
        ...owners.filter((entry) => entry.owner !== owner),
        { owner, callback },
      ]);
    },
    []
  );

  const unRegister = useCallback((owner: string, shortcut: string) => {
    const key = normalizeShortcut(shortcut);
    const owners = registryRef.current.get(key);
    if (!owners) return;
    // Keyed on the (shortcut, owner) PAIR — never on the shortcut alone, which
    // is the whole of defect (b): FilePlayer's cleanup used to delete
    // AudioEditorPanel's live registration and vice versa. The survivors keep
    // their order, so removing the newest re-exposes the previous owner.
    const survivors = owners.filter((entry) => entry.owner !== owner);
    if (survivors.length) {
      registryRef.current.set(key, survivors);
    } else {
      registryRef.current.delete(key);
    }
  }, []);

  const registry = useMemo(
    () => ({ register, unRegister }),
    [register, unRegister]
  );

  return (
    <HotkeyManagerContext.Provider value={registry}>
      {children}
    </HotkeyManagerContext.Provider>
  );
};

export default HotkeyManagerContextProvider;
